// GrillPatternsTab.js - Grill patterns configuration
import {
  React,
  useEffect,
  useState,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC,
  InfoBox
} from './GrillsCommon';
import { useProductScope } from '../utils/productScope';

const GrillPatternsTab = () => {
  const [rows, setRows] = useState([]);
  const [grillSizes, setGrillSizes] = useState([]);
  const [grillTypes, setGrillTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filterModel, setFilterModel] = useState({ items: [] });
  const { canSeeScope } = useProductScope();

  const fetchData = () => {
    setLoading(true);
    Promise.all([authFetch('/grills/patterns'), authFetch('/grills/sizes'), authFetch('/grills/types')])
      .then(([patterns, sizes, types]) => { setRows(patterns); setGrillSizes(sizes); setGrillTypes(types); setLoading(false); })
      .catch(err => { console.error('Failed to fetch:', err); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      const payload = { ...formData, fkGrillSize: formData.fkGrillSize || null };
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/grills/patterns/${editData.id}`, payload, 'PUT');
        setRows(prevRows => prevRows.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/grills/patterns', payload, 'POST');
        setRows(prevRows => [...prevRows, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);
    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this grill pattern?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/grills/patterns/${selectedRow.id}`, null, 'DELETE');
      setRows(prevRows => prevRows.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const getSizeName = (fkGrillSize) => {
    if (!fkGrillSize) return '(All Sizes)';
    const size = grillSizes.find(s => s.id === fkGrillSize);
    return size?.label || fkGrillSize;
  };

  const getTypeName = (fkGrillSize) => {
    if (!fkGrillSize) return '(All Types)';
    const size = grillSizes.find(s => s.id === fkGrillSize);
    if (!size || !size.fkGrillType) return '(All Types)';
    const type = grillTypes.find(t => t.id === size.fkGrillType);
    return type?.label || size.fkGrillType;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'grillType', headerName: 'Grill Type', width: 150,
      valueGetter: (value, row) => getTypeName(row.fkGrillSize),
      renderCell: (params) => getTypeName(params.row.fkGrillSize) },
    { field: 'fkGrillSize', headerName: 'Grill Size', width: 150,
      valueGetter: (value) => getSizeName(value),
      renderCell: (params) => getSizeName(params.row.fkGrillSize) },
    { field: 'label', headerName: 'Label', width: 150 },
    { field: 'value', headerName: 'Value', width: 150 },
    { field: 'applies_to', headerName: 'Applies To', width: 120 },
    { field: 'active', headerName: 'Active', width: 100,
      valueGetter: (value) => value ? 'Yes' : 'No',
      renderCell: (params) => params.row.active ? 'Yes' : 'No' },
    { field: 'sort_order', headerName: 'Sort Order', width: 100 }
  ];

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Pattern
        </Button>
        <Button variant="contained" color="secondary" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Pattern
        </Button>
        <Button variant="outlined" color="error" onClick={handleDelete} disabled={!selectedRow}>
          Delete Pattern
        </Button>
      </Stack>

      <InfoBox />

      {loading ? <CircularProgress /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={rows.filter((r) => canSeeScope(r.applies_to))}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
        />
      )}

      <GrillPatternDialog
        open={openDialog}
        data={editData}
        grillSizes={grillSizes}
        grillTypes={grillTypes}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const GrillPatternDialog = ({ open, data, grillSizes, grillTypes, onClose, onSave }) => {
  const { defaultScope, optionsForValue } = useProductScope();
  const [formData, setFormData] = useState({ fkGrillSize: '', label: '', value: '', applies_to: 'windows', active: true, sort_order: 0 });
  const [selectedGrillType, setSelectedGrillType] = useState('');

  useEffect(() => {
    if (data) {
      setFormData({ fkGrillSize: data.fkGrillSize || '', label: data.label || '', value: data.value || '', applies_to: data.applies_to || defaultScope, active: data.active ?? true, sort_order: data.sort_order || 0 });
      // Set the grill type filter based on the selected size's fkGrillType
      if (data.fkGrillSize) {
        const size = grillSizes.find(s => s.id === data.fkGrillSize);
        setSelectedGrillType(size?.fkGrillType || '');
      } else {
        setSelectedGrillType('');
      }
    } else {
      setFormData({ fkGrillSize: '', label: '', value: '', applies_to: defaultScope, active: true, sort_order: 0 });
      setSelectedGrillType('');
    }
  }, [data, open, grillSizes]);

  // Filter sizes based on selected grill type
  const filteredSizes = selectedGrillType
    ? grillSizes.filter(s => s.fkGrillType === selectedGrillType || !s.fkGrillType)
    : grillSizes;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Grill Pattern' : 'Add Grill Pattern'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Grill Type (filter)</InputLabel>
            <Select
              value={selectedGrillType}
              onChange={(e) => {
                setSelectedGrillType(e.target.value);
                setFormData({ ...formData, fkGrillSize: '' });
              }}
              label="Grill Type (filter)"
            >
              <MenuItem value="">(All Types)</MenuItem>
              {grillTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Grill Size (leave empty for all)</InputLabel>
            <Select value={formData.fkGrillSize} onChange={(e) => setFormData({ ...formData, fkGrillSize: e.target.value })} label="Grill Size (leave empty for all)">
              <MenuItem value="">(All Sizes)</MenuItem>
              {filteredSizes.map(s => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth />
          <TextField label="Value" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Applies To</InputLabel>
            <Select
              value={formData.applies_to}
              label="Applies To"
              onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
            >
              {optionsForValue(formData.applies_to).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} fullWidth />
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrillPatternsTab;
